import { Directive, HostListener, ElementRef, Input, Renderer2, OnDestroy, AfterViewInit } from '@angular/core'

/**
 * Directive to automatically select the content of an input field when it receives focus.
 *
 * Works on:
 * 1. Regular input elements directly.
 * 2. PrimeNG components like `p-autocomplete`, where the input is nested within the component.
 *
 * If the directive is attached to a non-input element (e.g., `p-autocomplete`), it can use the `elementId`
 * property to find the input element inside and select its content.
 *
 * Usage Scenarios:
 *
 * 1. <input type="text" jmlAutoSelect> => directive is enabled
 * 2. <input type="text" [jmlAutoSelect]="true">  => directive is enabled
 * 3. <input type="text" [jmlAutoSelect]="false"> => directive is disabled
 * 4. <p-autocomplete [jmlAutoSelect]="true" [elementId]="<YourInputId"></p-autocomplete> => directive is enabled
 *
 */
@Directive({
  selector: '[jmlAutoSelect]'
})
export class AutoSelectDirective implements AfterViewInit, OnDestroy {
  @Input() jmlAutoSelect = true

  // Optional elementId to locate a nested input element within PrimeNG or other components.
  @Input() elementId: string | null = null

  private focusListener: () => void
  private inputElement: HTMLInputElement | null = null

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit() {
    this.inputElement = this.findInputElement()

    if (this.inputElement) {
      // Listen to the 'focus' event on the found input element
      this.focusListener = this.renderer.listen(this.inputElement, 'focus', () => this.selectContent())
    } else {
      console.warn(`Input element not found for jmlAutoSelect.`)
    }
  }

  ngOnDestroy() {
    // Clean up the listener to avoid memory leaks
    if (this.focusListener) {
      this.focusListener()
    }
  }

  /**
   * Helper function to find the correct input element.
   * If the directive is attached directly to an input element, it returns that.
   * Otherwise, it looks for an input with the provided elementId.
   */
  private findInputElement(): HTMLInputElement | null {
    const nativeElement = this.el.nativeElement

    // Case 1: The host element is an input field
    if (this.isInputElement(nativeElement)) {
      return nativeElement
    }

    // Case 2: The host element is not an input, find the input by elementId
    if (this.elementId) {
      return nativeElement.querySelector(`#${this.elementId}`)
    }

    return null
  }

  // Select the content of the input field if `jmlAutoSelect` is `true`.
  private selectContent() {
    if (this.jmlAutoSelect && this.inputElement) {
      this.inputElement.select()
    }
  }

  // Helper method to check if the native element is an input element.
  private isInputElement(element: any): element is HTMLInputElement {
    return element.tagName && element.tagName.toLowerCase() === 'input'
  }
}
